import type { ContentRepository } from '@site/shared/ports'
import type {
  PageModel, HeaderModel, FooterModel, SiteSettingsModel,
  CategoryModel, MaterialSummary, MaterialModel, EventModel, NewsModel,
} from '@site/shared'
import { PayloadHttpClient } from './PayloadHttpClient'
import { mapPage } from './mappers/page'
import { mapHeader, mapFooter, mapSiteSettings } from './mappers/globals'
import { mapCategory, mapEvent, mapMaterial, mapMaterialSummary, mapNews } from './mappers/app'

type FindResp<T> = { docs: T[]; totalDocs: number }

export class PayloadContentRepository implements ContentRepository {
  /**
   * @param client  HTTP client bound to one content locale.
   * @param baseUrl PUBLIC CMS origin, used to absolutise media URLs so they
   *                resolve in the visitor's browser (not the internal API URL).
   */
  constructor(
    private readonly client: PayloadHttpClient,
    private readonly baseUrl: string,
  ) {}

  async getPage(slug: string): Promise<PageModel | null> {
    const resp = await this.client.getCollection<FindResp<Record<string, unknown>>>('pages', {
      'where[slug][equals]': slug,
      depth: 1,
      limit: 1,
    })
    const raw = resp.docs?.[0]
    return raw ? mapPage(raw as Parameters<typeof mapPage>[0], this.baseUrl) : null
  }

  async listPageSlugs(): Promise<string[]> {
    const resp = await this.client.getCollection<FindResp<{ slug?: string }>>('pages', {
      depth: 0,
      limit: 1000,
      'select[slug]': 'true',
    })
    return (resp.docs ?? []).flatMap((d) => (d.slug ? [d.slug] : []))
  }

  async getHeader(): Promise<HeaderModel> {
    const raw = await this.client.getGlobal<Record<string, unknown>>('header', { depth: 0 })
    return mapHeader(raw)
  }

  async getFooter(): Promise<FooterModel> {
    const raw = await this.client.getGlobal<Record<string, unknown>>('footer', { depth: 0 })
    return mapFooter(raw)
  }

  async getSiteSettings(): Promise<SiteSettingsModel> {
    const raw = await this.client.getGlobal<Record<string, unknown>>('siteSettings', { depth: 1 })
    return mapSiteSettings(raw, this.baseUrl)
  }

  // Collections with drafts: the REST API returns only published docs unless
  // `draft=true` is passed, so the app never shows an unpublished material.

  async listMaterials(filter: { category?: string; featured?: boolean } = {}): Promise<MaterialSummary[]> {
    const resp = await this.client.getCollection<FindResp<Record<string, unknown>>>('materials', {
      depth: 1,
      limit: 200,
      sort: '-updatedAt',
      'where[category.slug][equals]': filter.category,
      'where[featured][equals]': filter.featured === undefined ? undefined : String(filter.featured),
    })
    return (resp.docs ?? []).map((d) => mapMaterialSummary(d, this.baseUrl))
  }

  async getMaterial(slug: string): Promise<MaterialModel | null> {
    const resp = await this.client.getCollection<FindResp<Record<string, unknown>>>('materials', {
      'where[slug][equals]': slug,
      depth: 1,
      limit: 1,
    })
    const raw = resp.docs?.[0]
    return raw ? mapMaterial(raw, this.baseUrl) : null
  }

  async listCategories(): Promise<CategoryModel[]> {
    const resp = await this.client.getCollection<FindResp<Record<string, unknown>>>('categories', { depth: 0, limit: 100, sort: 'order' })
    return (resp.docs ?? []).flatMap((d) => mapCategory(d) ?? [])
  }

  async listUpcomingEvents(): Promise<EventModel[]> {
    // An event without an end counts as upcoming until it starts.
    const now = new Date().toISOString()
    const resp = await this.client.getCollection<FindResp<Record<string, unknown>>>('events', {
      depth: 0,
      limit: 50,
      sort: 'startsAt',
      'where[or][0][endsAt][greater_than]': now,
      'where[or][1][startsAt][greater_than]': now,
    })
    return (resp.docs ?? []).map(mapEvent)
  }

  async listNews(limit: number): Promise<NewsModel[]> {
    const resp = await this.client.getCollection<FindResp<Record<string, unknown>>>('news', { depth: 0, limit, sort: '-publishedAt' })
    return (resp.docs ?? []).map(mapNews)
  }
}
